import React from "react";

import { useQuery } from "@tanstack/react-query";
import { Download, MonitorPlay, Radio } from "lucide-react";
import { useTranslation } from "react-i18next";

import FastAPI from "@/src/api/FastAPI";
import ProjectAPI from "@/src/api/ProjectAPI";
import { ComboboxBase } from "@/src/components/form-fields/Combobox";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/src/components/ui/accordion";
import { Badge } from "@/src/components/ui/badge";
import { isGeoblockActive } from "@/src/utils/geoblock";
import {
  getRightCountryCount,
  hasActiveRestriction,
} from "@/src/utils/mediaRights";

import RightsRestrictionFields from "./RightsRestrictionFields";

import type { LucideIcon } from "lucide-react";
import type { JSX } from "react";

import type {
  MediaDistributionType,
  MediaRight,
} from "@/src/api/MediaAPI.types";
import type { ComboboxOption } from "@/src/components/form-fields/MultiCombobox";

const ALL_SCOPE_VALUE = "all";

const DISTRIBUTION_ICON_MAP: Record<MediaDistributionType, LucideIcon> = {
  storefront: MonitorPlay,
  fast: Radio,
  d2g: Download,
};

interface Scope {
  uuid: string;
  name: string;
}

function getScopeOptionList(
  allLabel: string,
  scopeList: Scope[],
): ComboboxOption[] {
  const sortedOptionList = scopeList
    .map((scope) => ({ value: scope.uuid, label: scope.name }))
    .toSorted((firstOption, secondOption) =>
      firstOption.label.localeCompare(secondOption.label),
    );
  return [{ value: ALL_SCOPE_VALUE, label: allLabel }, ...sortedOptionList];
}

interface ScopeConfig {
  optionList: ComboboxOption[];
  isLoading: boolean;
  scopeKey: "storefront_project_uuid" | "fast_channel_uuid";
}

interface RightScopeFieldProps {
  optionList: ComboboxOption[];
  value: string | null;
  isLoading: boolean;
  disabled?: boolean;
  onChange: (uuid: string | null) => void;
}

function RightScopeField({
  optionList,
  value,
  isLoading,
  disabled,
  onChange,
}: RightScopeFieldProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  return (
    <ComboboxBase
      disabled={disabled}
      emptyLabel={t("rightsScopeEmpty")}
      isLoading={isLoading}
      label={t("rightsScope")}
      loadingLabel={t("loading")}
      options={optionList}
      placeholder={t("rightsScope")}
      searchPlaceholder={t("rightsScopeSearch")}
      value={value ?? ALL_SCOPE_VALUE}
      onValueChange={(nextValue) =>
        onChange(nextValue === ALL_SCOPE_VALUE ? null : nextValue)
      }
    />
  );
}

interface RightSummaryProps {
  right: MediaRight;
  scopeLabel: string | null;
  countryCount: number;
}

function RightSummary({
  right,
  scopeLabel,
  countryCount,
}: RightSummaryProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const restrictionLabelList: string[] = [];
  if (isGeoblockActive(right.geoblock_mode)) {
    restrictionLabelList.push(
      t(right.geoblock_mode === "deny" ? "summaryGeoDeny" : "summaryGeoAllow", {
        count: countryCount,
      }),
    );
  }
  if (right.temporal_restriction) {
    restrictionLabelList.push(t("summaryTemporal"));
  }
  if (right.commercial_restriction) {
    restrictionLabelList.push(t("summaryCommercial"));
  }
  if (right.views_count !== null) {
    restrictionLabelList.push(
      t("summaryViewsCount", { count: right.views_count }),
    );
  }
  const hasRestriction = hasActiveRestriction(right);
  const badgeLabelList =
    hasRestriction && scopeLabel
      ? [scopeLabel, ...restrictionLabelList]
      : restrictionLabelList;

  return (
    <span className="flex flex-wrap gap-1.5">
      {badgeLabelList.map((badgeLabel) => (
        <Badge key={badgeLabel} variant="secondary">
          {badgeLabel}
        </Badge>
      ))}
      {hasRestriction ? null : (
        <Badge variant="outline">{t("summaryNone")}</Badge>
      )}
    </span>
  );
}

interface RightsAdvancedAccordionProps {
  rightList: MediaRight[];
  visibleDistributionTypeList: MediaDistributionType[];
  disabled?: boolean;
  onRightChange: (
    distributionType: MediaDistributionType,
    patch: Partial<MediaRight>,
  ) => void;
}

export default function RightsAdvancedAccordion({
  rightList,
  visibleDistributionTypeList,
  disabled,
  onRightChange,
}: RightsAdvancedAccordionProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const { data: projectData, isInitialLoading: isProjectLoading } = useQuery(
    ProjectAPI.generateQuery({
      fn: ProjectAPI.getProjectList,
      params: [],
      options: { enabled: visibleDistributionTypeList.includes("storefront") },
    }),
  );
  const { data: channelData, isInitialLoading: isChannelLoading } = useQuery(
    FastAPI.generateQuery({
      fn: FastAPI.getChannelsList,
      params: [],
      options: { enabled: visibleDistributionTypeList.includes("fast") },
    }),
  );

  const { data: countryGroupData } = useQuery(
    ProjectAPI.generateQuery({
      fn: ProjectAPI.getCountryGroupList,
      params: [],
      options: {
        enabled: rightList.some((right) =>
          isGeoblockActive(right.geoblock_mode),
        ),
      },
    }),
  );
  const countryGroupList = countryGroupData?.data ?? [];

  const scopeConfigMap: Record<"storefront" | "fast", ScopeConfig> = {
    storefront: {
      optionList: getScopeOptionList(t("allProjects"), projectData?.data ?? []),
      isLoading: isProjectLoading,
      scopeKey: "storefront_project_uuid",
    },
    fast: {
      optionList: getScopeOptionList(t("allChannels"), channelData?.data ?? []),
      isLoading: isChannelLoading,
      scopeKey: "fast_channel_uuid",
    },
  };

  const renderScopeField = (right: MediaRight): JSX.Element | null => {
    if (right.distribution_type === "d2g") return null;
    const { optionList, isLoading, scopeKey } =
      scopeConfigMap[right.distribution_type];
    return (
      <div className="max-w-[500px]">
        <RightScopeField
          disabled={disabled}
          isLoading={isLoading}
          optionList={optionList}
          value={right[scopeKey] ?? null}
          onChange={(uuid) =>
            onRightChange(right.distribution_type, { [scopeKey]: uuid })
          }
        />
      </div>
    );
  };

  const getScopeLabel = (right: MediaRight): string | null => {
    if (right.distribution_type === "d2g") return null;
    const { optionList, scopeKey } = scopeConfigMap[right.distribution_type];
    const selectedValue = right[scopeKey] ?? ALL_SCOPE_VALUE;
    return (
      optionList.find((option) => option.value === selectedValue)?.label ?? null
    );
  };

  const visibleRightList = rightList.filter((right) =>
    visibleDistributionTypeList.includes(right.distribution_type),
  );

  return (
    <Accordion
      className="flex flex-col gap-2"
      type="multiple"
      defaultValue={visibleRightList
        .filter(hasActiveRestriction)
        .map((right) => right.distribution_type)}
    >
      {visibleRightList.map((right) => {
        const Icon = DISTRIBUTION_ICON_MAP[right.distribution_type];
        return (
          <AccordionItem
            key={right.distribution_type}
            className="bg-card data-[state=open]:border-primary/50 rounded-lg border last:border-b"
            value={right.distribution_type}
          >
            <AccordionTrigger className="items-center px-4 py-3 hover:no-underline [&>svg]:translate-y-0">
              <span className="flex min-w-0 flex-1 items-center gap-3.5">
                <span
                  aria-hidden="true"
                  className="bg-muted flex size-8 shrink-0 items-center justify-center rounded-full"
                >
                  <Icon className="size-4" />
                </span>
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="min-w-0 truncate text-base font-semibold">
                    {t(`distribution_${right.distribution_type}`)}
                  </span>
                  <RightSummary
                    right={right}
                    scopeLabel={getScopeLabel(right)}
                    countryCount={
                      isGeoblockActive(right.geoblock_mode)
                        ? getRightCountryCount(right, countryGroupList)
                        : 0
                    }
                  />
                </span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-6 border-t px-4 py-4 sm:px-6">
              {renderScopeField(right)}
              <RightsRestrictionFields
                disabled={disabled}
                showViewsCount={right.distribution_type === "d2g"}
                value={right}
                onChange={(patch) =>
                  onRightChange(right.distribution_type, patch)
                }
              />
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
